import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, User, X } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { ContactFilterParams } from './ContactFilters';
import GetParticipantsQuery from './GetParticipantsQuery';
import UI from '../../../common/UI';
import useFocusIndex from '../../../common/useFocusIndex';
import useOnClickOutside from '../../../common/useOnClickOutside';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export interface Participant {
  id: string;
  full_name: string;
  email: string;
}

export type ContactPickerPreselector = (
  (participants: Participant[])=> Participant
);

export interface ContactPickerProps {
  participant?: Participant;
  filters?: ContactFilterParams;
  onChange?: (participant?: Participant) => void;
  preselect?: ContactPickerPreselector,
  renderActive?: (participant?: Participant) => ReactNode;
  nullable?: boolean;
  disabled?: boolean;
  noneOption?: ReactNode;
}

const ContactPicker = ({
  participant, filters, onChange, preselect, renderActive,
  nullable = true, disabled = false, noneOption,
}: ContactPickerProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const [limit, setLimit] = useState(20);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(filters?.search || '');
  const searchRef = useRef<HTMLInputElement>();

  const { event, ...inputFilters } = filters || {};

  const { data, loading } = useQuery(GetParticipantsQuery, {
    variables: {
      project_id: project.id,
      ...inputFilters,
      events: event ? [event] : null,
      search,
      per_page: limit,
    },
    skip: !preselect && !isOpen && !participant,
  });

  const participants = useMemo(
    () => data?.project.participants.data || [],
    [data?.project.participants.data],
  );

  const resetSearch = () => {
    setSearch('');

    if (searchRef.current) {
      searchRef.current.value = '';
    }
  };

  /** Index 0 is reserved for 'all participants' */
  const activeIndex = participants.findIndex(({ id }) => id === participant?.id) + (nullable ? 1 : 0);
  const maxIndex = participants.length - (nullable ? 0 : 1);

  /* eslint-disable @typescript-eslint/no-use-before-define */
  const [focusIndex, setFocusIndex, listRef, scrollToIndex] = useFocusIndex({
    initialIndex: Math.max(0, activeIndex),
    maxIndex,
    active: isOpen,
    onSelect: (index) => setParticipant(participants[index - (nullable ? 1 : 0)]?.id),
    onCancel: () => close(),
  });
  /* eslint-enable @typescript-eslint/no-use-before-define */

  const handleSearch = (participant: ChangeEvent<HTMLInputElement>) => {
    setSearch(participant.target.value);
  };

  useEffect(() => {
    const min = search ? Math.min(1, maxIndex) : 0;
    setFocusIndex(Math.max(min, activeIndex));
  }, [search, maxIndex, setFocusIndex, activeIndex, isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = () => {
    setIsOpen(true);
    scrollToIndex(focusIndex);
  };

  const setParticipant = useCallback((id?: string) => {
    onChange?.(participants.filter((participant) => participant.id === id)[0]);
    close();
  }, [onChange, participants, close]);

  const toggleDropdown = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    }
  }, [isOpen]);

  const containerRef = useRef<HTMLDivElement>();

  useOnClickOutside(containerRef, () => {
    close();
  });

  useEffect(() => {
    if (data && preselect) {
      setParticipant(preselect(participants)?.id);
    }
  }, [data, preselect, setParticipant, participants]);

  return (
    <UI.PickerContainer open={isOpen} ref={containerRef}>
      <UI.PickerDropdown open={isOpen}>
        <button type="button" onClick={() => toggleDropdown()} disabled={disabled}>
          <UI.Span>
            <UI.Truncate>
              {participant && (renderActive?.(participant) || (
                <UI.Span>
                  <UI.Icon>
                    <User />
                  </UI.Icon>
                  {' '}
                  {participant.full_name}
                </UI.Span>
              ))}
              {!participant && (noneOption || <UI.Span sc={{ color: 'gray.400' }}>{t('choose_contact')}</UI.Span>)}
            </UI.Truncate>
            <UI.Icon>
              <ChevronDown />
            </UI.Icon>
          </UI.Span>
        </button>
        {isOpen && (
          <UI.Div>
            <UI.InputGroup sc={{ mb: 2 }}>
              <UI.InputWrapper>
                <UI.Span>
                  <UI.Icon>
                    <Search />
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder={t('search')}
                  ref={searchRef}
                  sc={{ px: 5 }}
                />
                {search && (
                  <UI.Span>
                    <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                      <UI.Icon>
                        <X />
                      </UI.Icon>
                    </UI.A>
                  </UI.Span>
                )}
              </UI.InputWrapper>
            </UI.InputGroup>
            <UI.Div id="ContactPickerList" ref={listRef}>
              {nullable && (
                <>
                  <UI.PickerItem
                    type="button"
                    id="ConcactPickerList_0"
                    onClick={() => setParticipant(null)}
                    onFocus={() => setFocusIndex(0)}
                    onMouseEnter={() => setFocusIndex(0)}
                    active={activeIndex === 0}
                    focus={focusIndex === 0}
                    tabIndex={-1}
                  >
                    {noneOption || t('none')}
                  </UI.PickerItem>
                  <UI.HR />
                </>
              )}
              {!data && <UI.Loader />}
              {participants.map((participant, index) => (
                <UI.PickerItem
                  type="button"
                  id={`ContactPickerList_${index + (nullable ? 1 : 0)}`}
                  onClick={() => setParticipant(participant.id)}
                  onFocus={() => setFocusIndex(index + (nullable ? 1 : 0))}
                  onMouseEnter={() => setFocusIndex(index + (nullable ? 1 : 0))}
                  active={activeIndex === index + (nullable ? 1 : 0)}
                  focus={focusIndex === index + (nullable ? 1 : 0)}
                  tabIndex={-1}
                  style={{ opacity: loading ? 0.5 : undefined }}
                  key={participant.id}
                >
                  <UI.Delimit>
                    {participant.full_name}
                    {participant.email}
                  </UI.Delimit>
                </UI.PickerItem>
              ))}
              {data?.project.participants.has_more_pages && (
                <>
                  <UI.HR />
                  <UI.PickerItem
                    type="button"
                    id={`ContactPickerList_${participants.length + 1}`}
                    onClick={() => setLimit((limit) => limit + 20)}
                    onFocus={() => setFocusIndex(participants.length + 1)}
                    onMouseEnter={() => setFocusIndex(participants.length + 1)}
                    active={activeIndex === participants.length + 1}
                    focus={focusIndex === participants.length + 1}
                    tabIndex={-1}
                    disabled={loading}
                  >
                    {t('show_more')}
                  </UI.PickerItem>
                </>
              )}
            </UI.Div>
          </UI.Div>
        )}
      </UI.PickerDropdown>
    </UI.PickerContainer>
  );
};

export default ContactPicker;
